import React from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number, oneOf, shape, string } from 'prop-types'

import { NOOP } from 'Utils/stableDefaults'

import PlanCard from './Components/PlanCard'
import UsageTracker from './Components/UsageTracker'
import {
  ALLOWANCE_STATE, PLAN_TYPE, SUBSCRIPTION_STATUS, TIER, isRestructureLayout, resolveDisplayStatus,
} from '../../constants'
import { allowanceShape, planChangesShape, scheduledChangeShape } from '../../propTypes'

// Suspended and ended rows get an empty allowance from the BE; the row's own download counter fills it in.
function inactiveAllowance(allowance, downloadsUsed, downloadLimit) {
  const downloadsLeft = Math.max(downloadLimit - downloadsUsed, 0)

  return {
    ...allowance,
    monthlyAllowance: downloadLimit,
    downloadsAvailable: downloadsLeft,
    generationsAvailable: allowance.generationWeight > 0 ? Math.floor(downloadsLeft / allowance.generationWeight) : 0,
    resetsAt: null,
  }
}

// The BE reports no downloads while a payment fails; the card still shows what is left, per design.
function paymentFailedAllowance(allowance) {
  return { ...allowance, downloadsAvailable: Math.floor(allowance.remaining) }
}

function usageAllowance(allowance, isAllowanceInactive, downloadsUsed, downloadLimit) {
  if (isAllowanceInactive) return inactiveAllowance(allowance, downloadsUsed, downloadLimit)
  if (allowance?.state === ALLOWANCE_STATE.PAYMENT_FAILED) return paymentFailedAllowance(allowance)

  return allowance
}

function Subscription({
  subscription,
  locale = 'en',
  isUsageDisabled = false,
  isCancelLoading = false,
  onCancel: handleCancelSubscription = NOOP,
  onReactivate: handleReactivateSubscription = NOOP,
  onRenew: handleRenewSubscription = NOOP,
  isRestoreLoading = false,
  updatePaymentUrl = '/profile/subscription/update-payment-method',
  onRetryPayment: handleRetryPayment = NOOP,
  retryState = 'idle',
  onSwitchPlan: handleSwitchPlan = NOOP,
  subscriptionV16Enabled = false,
  onCancelPlanSwitch: handleCancelPlanSwitch = NOOP,
  isCancelPlanSwitchLoading = false,
  onResumePause: handleResumePause = NOOP,
  isResumeLoading = false,
  resumeRequested = false,
  resumeInProgress = false,
  plansRestructureEnabled = false,
}) {

  const {
    status,
    statusLabel,
    tier,
    canceledAt,
    canceledByAdmin,
    price,
    renewsOn,
    paymentMethod,
    periodEndDate,
    suspendedAt,
    downloadsUsed,
    downloadLimit,
    subscriptionEndDate,
    planType,
    billingCycle,
    scheduledPlanSwitchTo,
    scheduledPlanSwitchAt,
    userPauseScheduledAt,
    userPauseScheduledUntil,
    userPause,
    pausedUntil,
    billingTerm,
    termPrice,
    nextBillingDate,
    allowance,
    scheduledChange,
    planChanges,
  } = subscription
  const isRestructure = isRestructureLayout(subscription, plansRestructureEnabled)
  const isPaused = resolveDisplayStatus(status, canceledAt) === SUBSCRIPTION_STATUS.PAUSED
  const isPauseScheduled =
    resolveDisplayStatus(status, canceledAt, userPauseScheduledAt) === SUBSCRIPTION_STATUS.PAUSE_SCHEDULED
  const isEndedDuringPause = status === SUBSCRIPTION_STATUS.PAUSED && Boolean(canceledAt)
  const isAllowanceInactive = allowance?.state === ALLOWANCE_STATE.NONE || (Boolean(allowance) && isEndedDuringPause)

  return (
    <div className={classnames('subscription', isRestructure && 'subscription--restructure')}>
      <div className={classnames('subscription__container', isRestructure && 'subscription__container--restructure')}>
        <PlanCard
          status={status}
          statusLabel={statusLabel}
          tier={tier}
          canceledAt={canceledAt}
          canceledByAdmin={canceledByAdmin}
          price={price}
          renewsOn={renewsOn}
          paymentMethod={paymentMethod}
          subscriptionEndDate={subscriptionEndDate}
          periodEndDate={periodEndDate}
          suspendedAt={suspendedAt}
          accessEndsAt={allowance?.endsAt}
          onCancel={handleCancelSubscription}
          isCancelLoading={isCancelLoading}
          onReactivate={handleReactivateSubscription}
          onRenew={handleRenewSubscription}
          isRestoreLoading={isRestoreLoading}
          locale={locale}
          updatePaymentUrl={updatePaymentUrl}
          onRetryPayment={handleRetryPayment}
          retryState={retryState}
          planType={planType}
          billingCycle={billingCycle}
          onSwitchPlan={handleSwitchPlan}
          subscriptionV16Enabled={subscriptionV16Enabled}
          scheduledPlanSwitchTo={scheduledPlanSwitchTo}
          scheduledPlanSwitchAt={scheduledPlanSwitchAt}
          onCancelPlanSwitch={handleCancelPlanSwitch}
          isCancelPlanSwitchLoading={isCancelPlanSwitchLoading}
          userPauseScheduledAt={userPauseScheduledAt}
          userPauseScheduledUntil={userPauseScheduledUntil}
          userPause={userPause}
          pausedUntil={pausedUntil}
          onResumePause={handleResumePause}
          isResumeLoading={isResumeLoading}
          resumeRequested={resumeRequested}
          resumeInProgress={resumeInProgress}
          plansRestructureEnabled={isRestructure}
          billingTerm={billingTerm}
          termPrice={termPrice}
          nextBillingDate={nextBillingDate}
          scheduledChange={scheduledChange}
          planChanges={planChanges}
        />
        <aside className={classnames('subscription__aside', isRestructure && 'subscription__aside--restructure')}>
          <UsageTracker
            downloadsUsed={downloadsUsed}
            downloadLimit={downloadLimit}
            periodEndDate={periodEndDate}
            canceledByAdmin={canceledByAdmin}
            isDisabled={isUsageDisabled}
            isPaused={isPaused}
            locale={locale}
            tier={tier}
            plansRestructureEnabled={isRestructure}
            allowance={usageAllowance(allowance, isAllowanceInactive, downloadsUsed, downloadLimit)}
            isAllowanceInactive={isAllowanceInactive}
            pausedUntil={pausedUntil}
            pauseStartsAt={isPauseScheduled ? userPauseScheduledAt : null}
            canResume={isPaused && Boolean(userPause) && !resumeRequested}
            isResumeLoading={isResumeLoading}
            onResume={handleResumePause}
          />
        </aside>
      </div>
    </div>
  )
}

Subscription.propTypes = {
  subscription: shape({
    id: number,
    status: string.isRequired,
    statusLabel: string.isRequired,
    tier: oneOf([TIER.ESSENTIALS, TIER.PREMIUM, TIER.AI_STUDIO]),
    canceledAt: string,
    canceledByAdmin: bool,
    price: string.isRequired,
    renewsOn: string,
    paymentMethod: string.isRequired,
    periodEndDate: string,
    suspendedAt: string,
    downloadsUsed: number.isRequired,
    downloadLimit: number.isRequired,
    subscriptionEndDate: string,
    planType: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.YEARLY]),
    billingCycle: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.YEARLY]),
    scheduledPlanSwitchTo: string,
    scheduledPlanSwitchAt: string,
    userPauseScheduledAt: string,
    userPauseScheduledUntil: string,
    userPause: bool,
    pausedUntil: string,
    billingTerm: oneOf([PLAN_TYPE.MONTHLY, PLAN_TYPE.ANNUAL]),
    termPrice: number,
    nextBillingDate: string,
    allowance: allowanceShape,
    scheduledChange: scheduledChangeShape,
    planChanges: planChangesShape,
  }).isRequired,
  locale: string,
  isUsageDisabled: bool,
  onCancel: func,
  isCancelLoading: bool,
  onReactivate: func,
  onRenew: func,
  isRestoreLoading: bool,
  updatePaymentUrl: string,
  onRetryPayment: func,
  retryState: string,
  onSwitchPlan: func,
  subscriptionV16Enabled: bool,
  onCancelPlanSwitch: func,
  isCancelPlanSwitchLoading: bool,
  onResumePause: func,
  isResumeLoading: bool,
  resumeRequested: bool,
  resumeInProgress: bool,
  plansRestructureEnabled: bool,
}

export default Subscription
